// CC-02 · bloque B11 · El trámite con una carta por autoridad destino (AT del Sprint 3 §11.3.3 a
// §11.3.7, §11.5.4; spec de pantalla `ui/MG-ILE-1075.md`).
//
// UN TRÁMITE, UN FORMATO AMARILLO, N CARTAS (RN-77). `GET .../tramite` conserva en la raíz los
// campos de la carta (los de la primera activa por `orden`) y agrega `cartas[]` y `destinos`
// (§11.3.11). Cada carta trae los MISMOS campos de carta que la raíz, más su autoridad, su
// origen como destino (con quién, cuándo y por qué se ajustó), su reloj y, si toma algo de la
// predeterminada, el aviso de préstamo ya redactado por el servidor (RN-81). La pantalla no
// deriva nada de eso: lo pinta (§8.1 R1).
//
// CONTRATO REAL (backend de B5 a B7, `regulatorio/interfaces/http/esquemas.py`: `CartaSalida`,
// `DestinosSalida`, `TramiteSalida`), integrado el 5-oct-2026:
//   · `destinos.acciones_permitidas` NO depende del rol: trae `agregar` mientras los destinos
//     estén abiertos; quién puede agregar o quitar lo decide la ruta (403 fuera de rol o ámbito).
//   · `destinos.autoridades_para_agregar` llega vacío con los destinos cerrados.
//   · `texto_de_plantilla_prestada` de una versión viene en cada versión que usó la plantilla
//     prestada (bug #1085: cada versión lo recuerda); nulo si usó la propia o es adjunta.
//   · cada carta trae `motivo_ajuste`, `ajustado_por` y `ajustado_en` (ya resumidos en
//     `texto_del_destino`, que es lo que se pinta).
//   · agregar un destino responde 201 con el trámite; quitar, 200.
//
// QUÉ RUTA SE USA. Con `cartas[]` en el contrato, todo acto sobre una carta va por la familia
// `/regulatorio/casos/{caso}/cartas/{autoridad}/…` (ruta 73), también con un solo destino. Las
// rutas sin autoridad son el alias de la ÚNICA carta activa: con varias responden 409
// `transicion_no_permitida` («… indica la autoridad»). Sin `cartas[]` (o con la lista vacía), por
// esas rutas sin autoridad, como hasta el Sprint 3. `accionesDeLaCarta` elige una u otra y la
// pantalla no lo sabe.

import { pedir, pedirBinario, seg, segUuid } from './cliente';
import {
  descargarCargo, firmarYEnviar, prepararCarta, presentarCarta, registrarCargo, registrarFirma,
  registrarRemisionDelInformeFinal, solicitarFirma,
} from './regulatorio';
import type { Cargo, RelojDeBandeja, Tramite, VersionDeLaCarta } from './regulatorio';

/** Por qué una autoridad es destino del trámite (`carta_de_autoridad.origen_destino`, §11.3.3). */
export type OrigenDelDestino = 'registro' | 'predeterminada' | 'agregada';

/**
 * Los campos de UNA carta, con los mismos nombres que hoy tiene la raíz del trámite. Es lo que
 * pinta cada contenedor `carta-de-autoridad`; con un servidor anterior a B5, la raíz misma.
 */
export type CamposDeLaCarta = {
  estado_carta: string;
  estado_cargo: string;
  carta_version: number;
  plantilla_usada: string | null;
  carta_desactualizada: boolean;
  acciones_de_la_carta: string[];
  razon_de_firma_bloqueada?: string | null;
  versiones_de_la_carta: VersionDeLaCarta[];
  texto_de_la_carta: string | null;
  texto_de_la_espera: string | null;
  firma_solicitada_en: string | null;
  dias_esperando_firma: number | null;
  exige_profesional_responsable: boolean | null;
  firmante_responsable_configurado: string | null;
  firmante: string | null;
  presentado_por: string | null;
  fecha_presentacion: string | null;
  cargo: Cargo | null;
  texto_del_acuse: string | null;
  acuse_demorado: boolean;
  /** Con una carta por autoridad: ESTA carta tiene su cargo (la notificación a esta autoridad concluyó). */
  tramite_concluido: boolean;
  fecha_remision_informe_final: string | null;
  // --- CC-01 (`ui/MG-ILE-46.md`): por qué ESTA carta no se edita y el efecto sobre su firma ---
  razon_de_edicion_bloqueada?: string | null;
  texto_del_efecto_en_la_firma?: string | null;
};

/** Una carta del trámite (`cartas[]`, `CartaSalida` del backend; AT §11.3.3 y spec `MG-ILE-1075`). */
export type CartaDelTramite = CamposDeLaCarta & {
  autoridad_codigo: string;
  /** Nombre congelado al hacerse destino (RN-76). */
  autoridad_nombre: string;
  orden: number;
  /** `false`: la quitó Asuntos Regulatorios o el Representante del País (no se borra, RN-74). */
  activa: boolean;
  /** «Por confirmar con ILENDER» (D-1, R-CC2-3). */
  por_confirmar: boolean;
  origen_destino: OrigenDelDestino;
  /** «Registro del producto» · «Predeterminada del país» · «Agregada por Ana Ríos · 06/10/2026 · motivo» · «Quitada por …». */
  texto_del_destino: string;
  /** `quitar` mientras los destinos estén abiertos, la carta activa y no sea la última. */
  acciones_del_destino: string[];
  /** El último ajuste (agregada o quitada): ya va resumido en `texto_del_destino`. */
  motivo_ajuste: string | null;
  ajustado_por: string | null;
  ajustado_en: string | null;
  /** RN-81: el código de la predeterminada cuando la carta usa su plantilla o su plazo. */
  plantilla_prestada_de: string | null;
  texto_de_plantilla_prestada: string | null;
  plazo_prestado_de: string | null;
  texto_de_plazo_prestado: string | null;
  /** RN-81 ampliada (5-oct-2026): los días de acuse o de escalamiento son los de la predeterminada. */
  dias_de_aviso_prestados_de?: string | null;
  texto_de_dias_de_aviso_prestados?: string | null;
  /** El reloj de notificación de ESTA carta, con la forma del de la bandeja (CC2-24-C1); nulo si no corre. */
  notificacion: RelojDeBandeja | null;
};

/** Una autoridad activa del país que no es destino activo (también las quitadas: agregarla la reactiva). */
export type AutoridadParaAgregar = { codigo: string; nombre: string; por_confirmar: boolean };

/** Estado de los destinos del trámite (`DestinosSalida`; RN-74, AT §11.3.5). */
export type DestinosDelTramite = {
  /** Se cierran al preparar la primera carta (T-4). */
  cerrados: boolean;
  cerrados_en: string | null;
  /** «Puedes agregar o quitar autoridades…» · «Las cartas ya se empezaron a preparar: los destinos no cambian». */
  texto_del_estado: string;
  /**
   * `agregar` mientras los destinos estén abiertos, SIN mirar el rol: el permiso lo decide la
   * ruta (403 fuera de rol o de ámbito, CC2-20-C6). Vacío con los destinos cerrados.
   */
  acciones_permitidas: string[];
  /** Vacío con los destinos cerrados. */
  autoridades_para_agregar: AutoridadParaAgregar[];
};

/** El trámite con sus cartas por autoridad: lo que entregan `GET .../tramite` y cada acto desde B5. */
export type TramiteConCartas = Tramite & {
  cartas?: CartaDelTramite[];
  destinos?: DestinosDelTramite | null;
};

/**
 * La carta tal como la pinta un contenedor: sus campos y su autoridad. `autoridad_codigo` es
 * `null` cuando el servidor no trae `cartas[]` (anterior a B5): entonces es la raíz del trámite.
 */
export type VistaDeCarta = CamposDeLaCarta & {
  autoridad_codigo: string | null;
  autoridad_nombre: string | null;
  carta?: CartaDelTramite;
};

const DE_LA_RAIZ = (t: Tramite): VistaDeCarta => ({
  estado_carta: t.estado_carta, estado_cargo: t.estado_cargo, carta_version: t.carta_version,
  plantilla_usada: t.plantilla_usada, carta_desactualizada: t.carta_desactualizada,
  acciones_de_la_carta: t.acciones_de_la_carta, razon_de_firma_bloqueada: t.razon_de_firma_bloqueada ?? null,
  versiones_de_la_carta: t.versiones_de_la_carta, texto_de_la_carta: t.texto_de_la_carta ?? null,
  texto_de_la_espera: t.texto_de_la_espera, firma_solicitada_en: t.firma_solicitada_en,
  dias_esperando_firma: t.dias_esperando_firma, exige_profesional_responsable: t.exige_profesional_responsable,
  firmante_responsable_configurado: t.firmante_responsable_configurado, firmante: t.firmante,
  presentado_por: t.presentado_por, fecha_presentacion: t.fecha_presentacion, cargo: t.cargo,
  texto_del_acuse: t.texto_del_acuse, acuse_demorado: t.acuse_demorado, tramite_concluido: t.tramite_concluido,
  fecha_remision_informe_final: t.fecha_remision_informe_final,
  razon_de_edicion_bloqueada: t.razon_de_edicion_bloqueada ?? null,
  texto_del_efecto_en_la_firma: t.texto_del_efecto_en_la_firma ?? null,
  autoridad_codigo: null, autoridad_nombre: t.autoridad,
});

/**
 * El trámite trae la carta por autoridad (backend de B5 en adelante). Una lista VACÍA no cuenta:
 * el backend la entrega así si el trámite no tiene autoridad destino (`cartas` es `[]` por
 * defecto), y entonces la raíz sigue siendo la única carta, como hasta el Sprint 3.
 */
export const traeCartasPorAutoridad = (t: Tramite): t is TramiteConCartas & { cartas: CartaDelTramite[] } => {
  const cartas = (t as TramiteConCartas).cartas;
  return Array.isArray(cartas) && cartas.length > 0;
};

/**
 * Las cartas que se pintan, una por contenedor: las ACTIVAS de `cartas[]` en el orden en que
 * llegan (el servidor ordena por `orden`); sin `cartas[]` (o vacía), la raíz como única carta.
 */
export function cartasActivas(t: Tramite): VistaDeCarta[] {
  if (!traeCartasPorAutoridad(t)) return [DE_LA_RAIZ(t)];
  return t.cartas.filter((c) => c.activa).map((c) => ({ ...c, carta: c }));
}

/** Todas las cartas del trámite, también las quitadas (las lista el bloque de destinos). */
export const todasLasCartas = (t: Tramite): CartaDelTramite[] => (traeCartasPorAutoridad(t) ? t.cartas : []);

// ---- Destinos (rutas 71 y 72) -------------------------------------------------------------

/** CC2-20-C4: agrega una autoridad activa del país como destino, con motivo. Responde 201 con el trámite. */
export function agregarDestino(casoId: string, autoridadCodigo: string, motivo: string) {
  return pedir<TramiteConCartas>(`/regulatorio/casos/${segUuid(casoId)}/destinos`, {
    metodo: 'POST', cuerpo: { autoridad_codigo: autoridadCodigo, motivo },
  });
}

/** CC2-20-C4: quita un destino con motivo (la carta queda retirada, no se borra). Responde el trámite. */
export function quitarDestino(casoId: string, autoridadCodigo: string, motivo: string) {
  return pedir<TramiteConCartas>(`/regulatorio/casos/${segUuid(casoId)}/destinos/${seg(autoridadCodigo)}/retiro`, {
    metodo: 'POST', cuerpo: { motivo },
  });
}

// ---- Familia por carta (ruta 73) ----------------------------------------------------------

const deLaCarta = (casoId: string, autoridadCodigo: string) =>
  `/regulatorio/casos/${segUuid(casoId)}/cartas/${seg(autoridadCodigo)}`;

/** HU-20 por carta: prepara (o regenera) la carta de una autoridad. */
export function prepararCartaDe(casoId: string, autoridadCodigo: string, regenerar = false) {
  return pedir<TramiteConCartas>(deLaCarta(casoId, autoridadCodigo), { metodo: 'POST', cuerpo: { regenerar } });
}

export function solicitarFirmaDe(casoId: string, autoridadCodigo: string) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/solicitar-firma`, { metodo: 'POST', cuerpo: {} });
}

export function firmarYEnviarDe(casoId: string, autoridadCodigo: string, presentadoPor: string, fechaPresentacion?: string) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/firmar-y-enviar`, {
    metodo: 'POST', cuerpo: { presentado_por: presentadoPor, fecha_presentacion: fechaPresentacion ?? null },
  });
}

export function registrarFirmaDe(casoId: string, autoridadCodigo: string, firmante: string, fechaFirma: string) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/firma`, {
    metodo: 'POST', cuerpo: { firmante, fecha_firma: fechaFirma },
  });
}

export function presentarCartaDe(casoId: string, autoridadCodigo: string, presentadoPor: string, fechaPresentacion: string) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/presentacion`, {
    metodo: 'POST', cuerpo: { presentado_por: presentadoPor, fecha_presentacion: fechaPresentacion },
  });
}

/** El cuerpo del cargo, el mismo de hoy (HU-22-C1). */
export type CuerpoDelCargo = {
  referencia_autoridad: string; fecha_recepcion: string; nombre_archivo: string; contenido_base64: string;
  tipo_mime?: string | null; registrado_por?: string | null;
};

export function registrarCargoDe(casoId: string, autoridadCodigo: string, cuerpo: CuerpoDelCargo) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/cargo`, { metodo: 'POST', cuerpo });
}

export function descargarCargoDe(casoId: string, autoridadCodigo: string) {
  return pedirBinario(`${deLaCarta(casoId, autoridadCodigo)}/cargo/archivo`);
}

export function registrarRemisionDe(casoId: string, autoridadCodigo: string, fechaRemision: string) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/informe-final/remision`, {
    metodo: 'POST', cuerpo: { fecha_remision: fechaRemision },
  });
}

// ---- CC-01 · editar o adjuntar la carta antes de firmarla (rutas 63 a 65, AT S3 §10.4.3) ----
// Nacen con la forma POR CARTA: no hay alias sin autoridad.

/** CC-01-C1: el texto completo editado, sobre la versión vigente que se abrió (`version_base`). */
export function editarCartaDe(casoId: string, autoridadCodigo: string, cuerpo: { texto: string; motivo: string; version_base: number }) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/edicion`, { metodo: 'POST', cuerpo });
}

/** El cuerpo del adjunto: la carta final en base64, como el cargo (HU-22). */
export type CuerpoDelAdjunto = {
  nombre_archivo: string; contenido_base64: string; tipo_mime: string | null; motivo: string; version_base: number;
};

/** CC-01-C2: la carta final en PDF, redactada fuera del sistema. */
export function adjuntarCartaDe(casoId: string, autoridadCodigo: string, cuerpo: CuerpoDelAdjunto) {
  return pedir<TramiteConCartas>(`${deLaCarta(casoId, autoridadCodigo)}/adjunto`, { metodo: 'POST', cuerpo });
}

/** CC-01-C2 / C8: el PDF de una versión adjunta, tal como se subió. */
export function descargarVersionDe(casoId: string, autoridadCodigo: string, version: number) {
  return pedirBinario(`${deLaCarta(casoId, autoridadCodigo)}/versiones/${version}/archivo`);
}

/**
 * CC2-19-C1 (ruta 74): el HTML de impresión del Formato Amarillo de una versión con la caja de la
 * AC de esa autoridad. Se pide por el cliente, con el token, y no con un enlace: la navegación de
 * un enlace sale sin `Authorization` y con Entra la API responde 401 (BUG-1090). Lo abre en otra
 * pestaña `abrirDocumentoEnPestana`.
 */
export function imprimirFormatoAmarilloParaLaAutoridad(casoId: string, version: number, autoridadCodigo: string) {
  return pedirBinario(`/regulatorio/casos/${segUuid(casoId)}/formato-amarillo/versiones/${version}/imprimir?autoridad=${encodeURIComponent(autoridadCodigo)}`);
}

/** Los actos sobre UNA carta, con la misma firma para la ruta por carta y para el alias. */
export type AccionesDeLaCarta = {
  preparar: (regenerar: boolean) => Promise<Tramite>;
  solicitarFirma: () => Promise<Tramite>;
  firmarYEnviar: (presentadoPor: string, fecha: string) => Promise<Tramite>;
  registrarFirma: (firmante: string, fecha: string) => Promise<Tramite>;
  presentar: (presentadoPor: string, fecha: string) => Promise<Tramite>;
  registrarCargo: (cuerpo: CuerpoDelCargo) => Promise<Tramite>;
  descargarCargo: () => Promise<Blob>;
  registrarRemision: (fecha: string) => Promise<Tramite>;
  /** CC-01: solo por carta. Con un servidor anterior a B5 no se ofrecen (no trae `editar`). */
  editar: (cuerpo: { texto: string; motivo: string; version_base: number }) => Promise<Tramite>;
  adjuntar: (cuerpo: CuerpoDelAdjunto) => Promise<Tramite>;
  descargarVersion: (version: number) => Promise<Blob>;
};

/** CC-01 nace por carta (AT §10.4.3): sin autoridad no hay ruta a la que llamar. */
const sinRutaPorCarta = (): Promise<never> =>
  Promise.reject(new Error('Esta acción solo existe para la carta de una autoridad.'));

/**
 * Los actos de la carta de `autoridadCodigo`: por la familia por carta si el servidor la anuncia
 * (`autoridadCodigo` no nulo), o por las rutas vigentes sin autoridad. Las llamadas sin autoridad
 * son EXACTAMENTE las de hoy (mismos argumentos), para que nada cambie con un servidor anterior.
 */
export function accionesDeLaCarta(casoId: string, autoridadCodigo: string | null): AccionesDeLaCarta {
  if (autoridadCodigo === null) {
    return {
      preparar: (regenerar) => prepararCarta(casoId, regenerar),
      solicitarFirma: () => solicitarFirma(casoId),
      firmarYEnviar: (presentadoPor, fecha) => firmarYEnviar(casoId, presentadoPor, fecha),
      registrarFirma: (firmante, fecha) => registrarFirma(casoId, firmante, fecha),
      presentar: (presentadoPor, fecha) => presentarCarta(casoId, presentadoPor, fecha),
      registrarCargo: (cuerpo) => registrarCargo(casoId, cuerpo),
      descargarCargo: () => descargarCargo(casoId),
      registrarRemision: (fecha) => registrarRemisionDelInformeFinal(casoId, fecha),
      editar: sinRutaPorCarta,
      adjuntar: sinRutaPorCarta,
      descargarVersion: sinRutaPorCarta,
    };
  }
  return {
    preparar: (regenerar) => prepararCartaDe(casoId, autoridadCodigo, regenerar),
    solicitarFirma: () => solicitarFirmaDe(casoId, autoridadCodigo),
    firmarYEnviar: (presentadoPor, fecha) => firmarYEnviarDe(casoId, autoridadCodigo, presentadoPor, fecha),
    registrarFirma: (firmante, fecha) => registrarFirmaDe(casoId, autoridadCodigo, firmante, fecha),
    presentar: (presentadoPor, fecha) => presentarCartaDe(casoId, autoridadCodigo, presentadoPor, fecha),
    registrarCargo: (cuerpo) => registrarCargoDe(casoId, autoridadCodigo, cuerpo),
    descargarCargo: () => descargarCargoDe(casoId, autoridadCodigo),
    registrarRemision: (fecha) => registrarRemisionDe(casoId, autoridadCodigo, fecha),
    editar: (cuerpo) => editarCartaDe(casoId, autoridadCodigo, cuerpo),
    adjuntar: (cuerpo) => adjuntarCartaDe(casoId, autoridadCodigo, cuerpo),
    descargarVersion: (version) => descargarVersionDe(casoId, autoridadCodigo, version),
  };
}
